import React from "react";
import {
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { MoneyField } from "../../fields/MoneyField";
import { SectionAccordion } from "./SectionAccordion";
import ConstructionIcon from "@mui/icons-material/Construction";
import {
  LaborSectionProps,
  ChangeCountLaborSectionProps,
} from "../../../../types";

export interface LaborSectionComponentProps {
  state: LaborSectionProps;
  setState: (state: LaborSectionProps) => void;
  formErrors: Record<string, string>;
  updateField: (field: string, value: string) => void;
  total: number;
  mode: "edit" | "review";
  pageVariant: "create" | "edit" | "view";
  initialState?: LaborSectionProps;
  changeCount?: ChangeCountLaborSectionProps;
  forceMobile?: boolean;
}

export function LaborSection({
  state,
  setState,
  formErrors,
  updateField,
  total,
  mode,
  pageVariant,
  initialState,
  changeCount,
  forceMobile,
}: LaborSectionComponentProps) {
  const isMobile = forceMobile === true;
  const isEditView = pageVariant !== "create";
  const isView = pageVariant === "view";
  const viewFieldSx = {
    "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
    "& .MuiOutlinedInput-input.Mui-disabled": { WebkitTextFillColor: "rgba(0,0,0,0.45)" },
    "& .MuiInputAdornment-root .MuiTypography-root": { color: "rgba(0,0,0,0.45)" },
  };
  const labelXs = isMobile ? 12 : (isEditView ? 6 : 9);
  const fieldXs = isMobile ? 12 : 3;

  function initialText(val: number | undefined): string {
    return val === undefined ? "" : val.toString().replace(/^0+/, "");
  }

  function changeCountText(count: number): string {
    return count > 1 ? ` (${count})` : "";
  }

  return (
    <SectionAccordion
      id="labor"
      title="Labor"
      icon={
        <ConstructionIcon
          fontSize="large"
          sx={{ color: "#ACACAC" }}
        />
      }
      total={total}
      forceMobile={forceMobile}
    >
      {/* Labor Budget/Category A */}
      <Grid item xs={labelXs} display="flex">
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Labor Budget/Category A
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.LaborBudget)}
            {changeCountText(changeCount?.LaborBudget ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            disabled
            value={state.LaborBudget ?? ""}
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment> }}
            sx={viewFieldSx}
          />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.LaborBudget === undefined ? "" : state.LaborBudget.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("LaborBudget", e.target.value)}
            error={formErrors["LaborBudget"] !== ""}
            helperText={formErrors["LaborBudget"]}
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment> }}
          />
        ) : (
          <MoneyField
            label="Labor Budget/Category A"
            value={state.LaborBudget ?? null}
            onChange={(v) => updateField("LaborBudget", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["LaborBudget"] !== ""}
            helperText={formErrors["LaborBudget"]}
          />
        )}
      </Grid>

      {/* Extra Labor Actual/Category B */}
      <Grid item xs={labelXs} display="flex">
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Extra Labor Actual/Category B
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.ExtraLaborActual)}
            {changeCountText(changeCount?.ExtraLaborActual ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            disabled
            value={state.ExtraLaborActual ?? ""}
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment> }}
            sx={viewFieldSx}
          />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.ExtraLaborActual === undefined ? "" : state.ExtraLaborActual.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("ExtraLaborActual", e.target.value)}
            error={formErrors["ExtraLaborActual"] !== ""}
            helperText={formErrors["ExtraLaborActual"]}
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment> }}
          />
        ) : (
          <MoneyField
            label="Extra Labor Actual/Category B"
            value={state.ExtraLaborActual ?? null}
            onChange={(v) => updateField("ExtraLaborActual", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["ExtraLaborActual"] !== ""}
            helperText={formErrors["ExtraLaborActual"]}
          />
        )}
      </Grid>

      {/* Special Labor Budget */}
      <Grid item xs={labelXs} display="flex">
        <Typography
          variant="body2"
          color="text.secondary"
          fontWeight={400}
          sx={{ alignSelf: "center" }}
        >
          Special Labor Budget
        </Typography>
      </Grid>
      {isEditView && !isMobile && (
        <Grid item xs={fieldXs}>
          <Typography variant="body2" color="text.secondary" fontWeight={400} sx={{ alignSelf: "center" }}>
            {initialText(initialState?.SpecialLabor)}
            {changeCountText(changeCount?.SpecialLabor ?? 0)}
          </Typography>
        </Grid>
      )}
      <Grid item xs={fieldXs}>
        {isView ? (
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            disabled
            value={state.SpecialLabor ?? ""}
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment> }}
            sx={viewFieldSx}
          />
        ) : isEditView ? (
          <TextField
            disabled={mode === "review"}
            variant="outlined"
            size="small"
            type="text"
            inputProps={{ inputMode: "numeric", min: 0 }}
            value={state.SpecialLabor === undefined ? "" : state.SpecialLabor.toString().replace(/^0+/, "")}
            onChange={(e) => updateField("SpecialLabor", e.target.value)}
            error={formErrors["SpecialLabor"] !== ""}
            helperText={formErrors["SpecialLabor"]}
            fullWidth
            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ ml: 1 }}>$</InputAdornment> }}
          />
        ) : (
          <MoneyField
            label="Special Labor Budget"
            value={state.SpecialLabor ?? null}
            onChange={(v) => updateField("SpecialLabor", v === null ? "" : String(v))}
            disabled={mode === "review"}
            allowNegative={false}
            min={0}
            max={9999999.9999}
            error={formErrors["SpecialLabor"] !== ""}
            helperText={formErrors["SpecialLabor"]}
          />
        )}
      </Grid>

      {/* Total Labor */}
      <Grid item xs={isMobile ? 12 : 9} display="flex" justifyContent={isMobile ? "flex-start" : "flex-end"}
        mt={5}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ alignSelf: "center" }}
        >
          Total Labor
        </Typography>
      </Grid>
      <Grid item xs={fieldXs} mt={isMobile ? 0 : 5}>
        <TextField
          type="number"
          inputProps={{
            min: 0,
          }}
          disabled
          variant="filled"
          size="small"
          fullWidth
          value={total}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 1 }}>
                $
              </InputAdornment>
            ),
          }}
        />
      </Grid>
    </SectionAccordion>
  );
}
